import { ProductCode } from '~/types'
import type { IconLibProps } from '~/components/tools/icon.vue'
import { ProductPlatforms } from '~/server/type'

export const useUtils = () => {
	const mixpanel = useMixpanel()
	const isIndividual = computed(() => String(useRoute().name).toLowerCase().includes('individual'))
	const urlQuery = computed(() => {
		return useRoute().query as Record<string, string>
	})
	/**
	 * @link https://www.mycovergenius.com/
	 * */
	const baseUrl = computed(() => {
		if (import.meta.client) {
			return `${location.protocol}//${location.host}`
		}
		return 'https://www.mycovergenius.com'
	})
	const productCategoryType = (arg?: string, planName = '') => {
		if (!arg) {
			return null
		}
		const category = ref(String(arg).toLowerCase())
		const plan = ref<string | number>(String(planName).toLowerCase())

		return {
			isHealth: category.value === 'health',
			isAuto: category.value === 'auto',
			isComprehensive:
				plan.value === 'comprehensive cover' || plan.value === String(ProductCode.COMPREHENSIVE_AUTO),
			isTravel:
				category.value === 'travel' ||
				plan.value === String(ProductCode.TRAVEL_AIICO) ||
				plan.value === String(ProductCode.TRAVEL_ALLIANZ),
			isGadget: category.value === 'gadget' || plan.value === String(ProductCode.GADGET),
			isLaptop: category.value === 'laptop' || plan.value === String(ProductCode.LAPTOP_COVER),
			isThirdParty: plan.value === 'third party' || plan.value === String(ProductCode.THIRD_PARTY),
			isOthers: category.value === 'other'
		}
	}
	const productIcon = (productName: string): IconLibProps | null => {
		const pName = String(productName).toLowerCase()

		if (['flexicare', 'zencare', 'primecare'].includes(pName)) {
			return {
				icon: 'heart-2',
				dir: 'fill'
			}
		} else if (pName === 'gadget') {
			return {
				icon: 'monitor-mobile',
				dir: 'random',
				height: 14,
				width: 14,
				pathClass: 'fill-main'
			}
		} else if (pName === 'travel cover') {
			return {
				icon: 'airplane',
				dir: 'fill'
			}
		} else {
			return {
				icon: 'bus-transport',
				dir: 'fill'
			}
		}
	}
	const sourceChannel = computed(() => {
		const { query } = useRoute()
		let channelItems: ChannelsStorage | {} = {}

		if (import.meta.client) {
			const channelStorage = localStorage.getItem('channels')
			if (channelStorage) {
				channelItems = JSON.parse(channelStorage)
			}
		}
		return { ...query, ...channelItems }
	})
	const coverPeriod = (period: string) => {
		const periods = String(period).toLowerCase()
		const list: Record<string, number> = {
			monthly: 1,
			quarterly: 3,
			'bi-annual': 6,
			annual: 12
		}
		return list?.[periods] ?? 1
	}
	const wordLimit = (text: string, limit: number = 12) => {
		if (import.meta.client) {
			const words = String(text).split(' ')
			// Extract the first 12 words
			const first12Words = words.slice(0, limit)
			if (words.length > limit) {
				return first12Words.join(' ') + '...'
			}
		}

		return text
	}
	const isValidJSON = (str: string) => {
		try {
			JSON.parse(str)
			return true
		} catch (e) {
			return false
		}
	}
	const isEncrypted = (str: string): boolean => {
		try {
			atob(str)
			return true
		} catch (e) {
			return false
		}
	}
	const decrypt = <T>(arg: string) => {
		if (!arg) return null
		if (isEncrypted(arg)) {
			const value = atob(arg)
			if (isValidJSON(value)) {
				return JSON.parse(value) as T
			}
			return value as T
		} else if (isValidJSON(arg)) {
			return JSON.parse(arg) as T
		}
		return arg as T
	}
	const encrypt = <T = Object>(arg: T) => {
		let parameter = arg as any
		if (typeof arg === 'object') {
			parameter = JSON.stringify({ ...arg })
		}
		return btoa(parameter)
	}
	const userType = computed(() => {
		const { query } = useRoute()
		return String(query.userType ?? ProductPlatforms.BUSINESS).toLowerCase() as
			| ProductPlatforms.BUSINESS
			| ProductPlatforms.INDIVIDUAL
	})
	return {
		baseUrl,
		productCategoryType,
		productIcon,
		isIndividual,
		sourceChannel,
		coverPeriod,
		urlQuery,
		wordLimit,
		userType,
		isValidJSON,
		isEncrypted,
		decrypt,
		encrypt,
		...(mixpanel || {})
	}
}
